import { useEffect, useState } from "react";

import { STATSIG_CLIENT_KEY } from "@/config";
import { useGateValue, useStatsigClient } from "@statsig/react-bindings";

type UseFeatureGateOptions = {
  /**
   * If the SDK has not settled within this many ms, the gate settles to off
   * for the rest of this mount, even if the SDK settles on later. For a
   * caller that waits on the gate before doing something (a request), so a
   * slow or unreachable Statsig can hold it only this long, and a late answer
   * cannot make it do the thing twice. Omitted: wait for the SDK.
   */
  timeoutMs?: number;
};

/**
 * Wrapper around the Statsig feature-gate hook.
 *
 * A gate is a kill switch, not an experiment: there is no arm to remember, so
 * unlike `useExperiment` this does not replay a previous evaluation. It does
 * share the exposure discipline: the render-time check never logs, and the
 * exposure that counts is logged once the client holds values it trusts (see
 * `useExperiment` for why a "Loading" evaluation must not reach Statsig).
 *
 * `isEnabled` is `false` while the SDK is still loading, so a gated surface
 * stays hidden rather than flashing in and out. Every gate is off by default.
 * `isLoading` is `true` only while the FIRST answer is still coming: never
 * without a Statsig client key (then there is no provider, and the SDK's
 * default context would otherwise report loading forever), never after
 * `timeoutMs`, and never again once the SDK has settled for this mount.
 *
 * The SDK re-enters "loading" every time the Statsig user is updated, and
 * `useMe` does that from every component that loads the current user, so one
 * page view can reload the SDK several times. A gate that reported loading on
 * each of those would make a caller that waits on it (a request) run again;
 * instead the settled answer is kept until the SDK settles on a new one.
 */
export const useFeatureGate = (
  gateName: string,
  { timeoutMs }: UseFeatureGateOptions = {}
) => {
  const value = useGateValue(gateName, { disableExposureLog: true });
  const { client, isLoading } = useStatsigClient();
  const [timedOut, setTimedOut] = useState(false);
  /** The last value read while the SDK was NOT loading, once there is one. */
  const [settled, setSettled] = useState<{ value: boolean } | null>(null);

  useEffect(() => {
    if (!isLoading) {
      setSettled(previous =>
        previous?.value === value ? previous : { value }
      );
    }
  }, [isLoading, value]);

  /** Mirrors `ExperimentsProvider`: without a key it renders no provider at all. */
  const hasClient = !!STATSIG_CLIENT_KEY;
  const waitingForSdk = hasClient && isLoading && !timedOut && settled === null;

  useEffect(() => {
    if (!waitingForSdk || timeoutMs === undefined) {
      return;
    }

    const timer = window.setTimeout(() => setTimedOut(true), timeoutMs);

    return () => window.clearTimeout(timer);
  }, [waitingForSdk, timeoutMs]);

  /* A gate that timed out was shown as off, so a late evaluation is not an exposure to it. */
  useEffect(() => {
    if (isLoading || timedOut) {
      return;
    }

    client.checkGate(gateName);
  }, [client, isLoading, timedOut, gateName]);

  if (!hasClient || timedOut) {
    return { isEnabled: false, isLoading: false };
  }

  if (isLoading) {
    /* A reload after the first answer keeps that answer; before it, the gate is off. */
    return { isEnabled: settled?.value ?? false, isLoading: settled === null };
  }

  return { isEnabled: value, isLoading: false };
};
